import { useState } from "react";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { formatBRL } from "@/lib/utils";
import { resolveTicketQrCode } from "@/lib/ticket-qr";
import { formatEventDateTimeRange, getEventTimeZoneLabel, resolveEventTimeZone } from "@/lib/event-timezone";

export interface TicketDownloadData {
  ticketId: string;
  tierName: string;
  tierPrice?: number;
  attendeeName?: string;
  attendeeCpf?: string;
  qrCode: string;
  qrCodeImageUrl?: string | null;
  eventTitle: string;
  eventDate: string;
  eventEndDate?: string;
  eventTimeZone?: string | null;
  venueName?: string;
  venueAddress?: string;
  venueCity?: string;
  venueState?: string;
  isOnline?: boolean;
  streamingRedirectUrl?: string;
  coverImageUrl?: string | null;
  orderCode: string;
  purchaseDate?: string;
  paymentMethod?: string;
  eventDescription?: string;
  index: number;
}

export function TicketDownloadCard(props: TicketDownloadData) {
  const { t } = useTranslation("buyer");
  const {
    ticketId,
    tierName,
    tierPrice,
    attendeeName,
    attendeeCpf,
    qrCode,
    qrCodeImageUrl,
    eventTitle,
    eventDate,
    eventEndDate,
    eventTimeZone,
    venueName,
    venueAddress,
    venueCity,
    venueState,
    isOnline,
    orderCode,
    purchaseDate,
  } = props;

  const [downloading, setDownloading] = useState(false);
  const resolvedQrCode = resolveTicketQrCode(qrCode, ticketId);
  const resolvedTimeZone = resolveEventTimeZone(eventTimeZone);

  const qrImageUrl =
    qrCodeImageUrl ||
    `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(resolvedQrCode)}`;

  const formattedDateTime = formatEventDateTimeRange(eventDate, eventEndDate, resolvedTimeZone);
  const timeZoneLabel = getEventTimeZoneLabel(resolvedTimeZone);

  const locationParts = isOnline
    ? [t("ticketDetail.onlineAccess")]
    : [venueName, venueAddress, venueCity, venueState].filter(Boolean);
  const locationLine = locationParts.join(", ");

  const purchaseDateFormatted = purchaseDate
    ? new Date(purchaseDate).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Sao_Paulo",
    })
    : null;

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // jsPDF é pesado (~250KB) — importar só quando o usuário realmente clicar
      const { generateTicketPDF } = await import("@/lib/ticket-pdf");
      await generateTicketPDF(props);
    } catch (err) {
      console.error("PDF generation error:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4 w-full max-w-2xl mx-auto">
      {/* Visual ticket preview card */}
      <div className="rounded-[1.5rem] overflow-hidden border border-border bg-card">
        <div className="p-6 sm:p-8 space-y-6 relative">
          {/* Event title */}
          <div className="space-y-1">
            <p className="text-[10px] text-muted-foreground font-medium">{t("ticketDetail.card.headerLabel")}</p>
            <h2 className="font-display font-semibold text-foreground text-xl leading-tight">
              {eventTitle}
            </h2>
          </div>

          {/* Date, time, location row */}
          <div className="space-y-3 bg-muted/20 p-4 rounded-xl border border-border/50">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-foreground">{formattedDateTime}</span>
              <span className="text-[10px] text-muted-foreground font-medium">{timeZoneLabel || t("ticketDetail.card.localTime")}</span>
            </div>
            
            <div className="pt-2 border-t border-border/40">
              <span className="text-xs text-muted-foreground leading-relaxed">{locationLine || t("ticketDetail.venueToConfirm")}</span>
            </div>
          </div>

          {/* Dashed divider */}
          <div className="relative py-2">
            <div className="border-t-2 border-dashed border-border/40 w-full" />
          </div>

          {/* Ticket info + QR */}
          <div className="flex gap-6 items-start">
            <div className="flex-1 min-w-0 space-y-4 pt-1">
              {/* Tier */}
              <div className="space-y-1">
                <p className="text-[9px] text-muted-foreground font-medium">{t("ticketDetail.card.category")}</p>
                <p className="font-medium text-foreground text-base tracking-tight">{tierName}</p>
                {tierPrice != null && tierPrice > 0 && (
                  <p className="text-primary font-medium text-sm">{formatBRL(tierPrice)}</p>
                )}
              </div>

              {/* Attendee */}
              {attendeeName && (
                <div className="space-y-1">
                  <p className="text-[9px] text-muted-foreground font-medium">{t("ticketDetail.card.attendee")}</p>
                  <p className="font-medium text-foreground text-sm truncate">{attendeeName}</p>
                  {attendeeCpf && (
                    <p className="text-[10px] font-mono text-muted-foreground/60">
                      {attendeeCpf}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* QR code */}
            <div className="shrink-0 flex flex-col items-center gap-2">
              <div className="w-24 h-24 sm:w-28 sm:h-28 bg-white rounded-xl border border-muted p-1.5 flex items-center justify-center">
                <img
                  src={qrImageUrl}
                  alt="QR Code"
                  className="w-full h-full object-contain grayscale"
                  crossOrigin="anonymous"
                />
              </div>
              <p className="text-[9px] font-mono text-muted-foreground">
                #{ticketId.slice(0, 8).toUpperCase()}
              </p>
            </div>
          </div>

          <div className="border-t border-border/40 pt-4 mt-2">
            {/* Purchase info footer */}
            <div className="flex items-center justify-between text-[9px] text-muted-foreground font-medium">
              <span>{t("ticketDetail.card.orderNumber", { code: orderCode })}</span>
              {purchaseDateFormatted && (
                <span>{purchaseDateFormatted}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF button */}
      <Button
        variant="default"
        size="lg"
        className="w-full h-12 text-sm rounded-xl font-medium"
        onClick={handleDownloadPDF}
        disabled={downloading}
      >
        <Download className="h-4 w-4 mr-2" />
        {downloading ? t("ticketDetail.card.downloadingPdf") : t("ticketDetail.card.downloadPdf")}
      </Button>
    </div>
  );
}
